import { api } from "./api";

/*
 * KEEP THIS DEVICE'S PHONE ALERTS WORKING. Runs once each time the CRM opens.
 *
 * A browser's push subscription is bound to the server key (VAPID) it was made
 * with. Rotate that key on Railway and every phone silently stops hearing the
 * CRM: the push service refuses each send ("the VAPID credentials ... do not
 * correspond"), and the phone itself never finds out. That is what happened
 * after the 2026-10-22 rotation: twelve alerts lost, Ciridae's reply among them.
 *
 * So on open, a device that already said yes (permission granted, a
 * subscription held) checks its key against the server's. If they differ it
 * drops the old subscription and makes a new one, which needs no tap because
 * permission is already granted. Either way it hands the server its current
 * subscription, so a row the server deleted comes back without anyone noticing
 * it was gone. A device that never turned alerts on is left alone.
 */

export function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** Same bytes? A subscription that cannot say which key it holds counts as a match. */
export function sameKey(subscription, publicKey) {
  const held = subscription && subscription.options && subscription.options.applicationServerKey;
  if (!held || !publicKey) return true;
  const a = new Uint8Array(held);
  const b = urlBase64ToUint8Array(publicKey);
  return a.length === b.length && a.every((byte, i) => byte === b[i]);
}

/**
 * The subscription this device should hold for `publicKey`: the one it has if
 * the key matches, else a fresh one (the stale one is dropped on both sides).
 */
export async function subscriptionFor(reg, publicKey) {
  const current = await reg.pushManager.getSubscription();
  if (current && sameKey(current, publicKey)) return { subscription: current, renewed: false };
  if (current) {
    await api("/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint: current.endpoint }) }).catch(
      () => {}
    );
    await current.unsubscribe();
  }
  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  return { subscription, renewed: !!current };
}

let checked = false;

export async function repairPushSubscription() {
  if (checked) return null;
  checked = true;
  try {
    if (!pushSupported() || Notification.permission !== "granted") return null;
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg || !(await reg.pushManager.getSubscription())) return null;
    const status = await api("/push/status");
    if (!status.configured || !status.public_key) return null;
    const { subscription, renewed } = await subscriptionFor(reg, status.public_key);
    await api("/push/subscribe", { method: "POST", body: JSON.stringify({ subscription: subscription.toJSON() }) });
    if (renewed) console.log("Phone alerts: the server key changed, so this device subscribed again");
    return { renewed };
  } catch (err) {
    console.warn("Phone alerts: could not check this device's subscription:", err.message);
    return null;
  }
}
